import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { adminDashboardStats } from "@/lib/admin-console.functions";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_authenticated/admin/")({
  head: () => ({
    meta: [
      { title: "Admin Dashboard" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: DashboardPage,
});

function DashboardPage() {
  const fetchStats = useServerFn(adminDashboardStats);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "dashboard"],
    queryFn: () => fetchStats(),
    staleTime: 30_000,
  });

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Overview of leads, conversions, and activity.</p>
      </header>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}

      {data && (
        <>
          <div className="grid gap-4 grid-cols-2 md:grid-cols-4 mb-8">
            <StatCard label="Total leads" value={data.totals.allLeads} />
            <StatCard label="Last 7 days" value={data.totals.last7} />
            <StatCard label="Last 30 days" value={data.totals.last30} />
            <StatCard
              label="Conversion rate"
              value={`${data.conversionRate.toFixed(1)}%`}
              hint={`${data.statusCounts.won ?? 0} won`}
            />
          </div>

          <section className="grid gap-6 md:grid-cols-2">
            <div className="rounded-md border p-4">
              <h2 className="text-sm font-semibold mb-3">Pipeline</h2>
              <ul className="space-y-2 text-sm">
                {["new", "contacted", "qualified", "won", "lost"].map((s) => (
                  <li key={s} className="flex justify-between border-b pb-1 last:border-0">
                    <span className="capitalize">{s}</span>
                    <span className="font-medium">{data.statusCounts[s] ?? 0}</span>
                  </li>
                ))}
              </ul>
              <div className="text-xs text-muted-foreground mt-3">
                Emails sent (last 7d): {data.totals.emailsLast7}
              </div>
            </div>

            <div className="rounded-md border p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold">Recent submissions</h2>
                <Link to="/admin/leads" className="text-xs underline text-muted-foreground">
                  View all
                </Link>
              </div>
              <ul className="space-y-2 text-sm">
                {data.recent.length === 0 && (
                  <li className="text-muted-foreground">No submissions yet.</li>
                )}
                {data.recent.map((r: any) => (
                  <li key={r.id} className="flex items-start justify-between gap-2 border-b pb-2 last:border-0">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{r.full_name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {r.business_name} · {r.city}, {r.state}
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="text-[10px]">{r.follow_up_status}</Badge>
                      <div className="text-[10px] text-muted-foreground mt-1">
                        {new Date(r.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div className="rounded-md border p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}
